import { AnyObject } from '@vimeo/core/types/generic';

import { firebaseConfig } from '../../../configs/FirebaseConfig';
import { liveApplicationConfig } from '../../../configs/LiveApplicationConfig';
import { IFirebaseComposerCredentials } from '../../../types/broadcaster';
import { IFirebaseDependencies, TFirebaseApp, TFirebaseSnapshot } from '../../../types/firebase';
import {
  createFirebaseApplication,
  generateFirebaseApplicationName,
} from '../../../utils/firebase/management';
import { ELiveErrorCategory, trackLiveAction, trackLiveError } from '../../../utils/tracking';
import {
  ELiveRealtimeSignal,
  ELiveSignal,
  TComposerSessionReadySignalEvent,
  TFirebaseComposerAppReadySignalEvent,
} from '../../signals';
import { AbstractFirebaseManager } from './AbstractFirebaseManager';

export async function tryToSetupComposerConnection<T>(
  manager: AbstractFirebaseManager<T>,
): Promise<void> {
  const { composerCredentials } = manager.context;

  if (manager.firebase && composerCredentials && !manager.composerApp) {
    await createComposerConnection(manager, manager.firebase, composerCredentials);
  }
}

/**
 * Create composer firebase application and subscribe to connection/auth statuses.
 */
export async function createComposerConnection<T>(
  manager: AbstractFirebaseManager<T>,
  firebase: IFirebaseDependencies,
  credentials: IFirebaseComposerCredentials,
): Promise<void> {
  const { config, token, sessionId, sessionFolder, appName } = credentials;
  const metadata: AnyObject = { sessionId, appName };

  manager.log.info('Setting up composer firebase connections', sessionId);
  trackLiveAction('fb_composer_connection_create');

  try {
    let composerAppReadyNotified: boolean = false;

    const [app, unSubscribers] = await createFirebaseApplication(
      firebase,
      {
        config,
        name: appName,
        token,
        persistence: firebaseConfig.AUTH.DEFAULT_PERSISTENCE_TYPE,
      },
      {
        onPersistenceChangeFinished: (persistence) => {
          manager.log.info('Composer auth persistence state set to:', persistence);
        },
        onPersistenceChangeFailed: (error, persistence) => {
          manager.log.error('Composer auth persistence state change failed:', error, persistence);
        },
        onBeforeAuthStateChanged: (firebaseUser) => {
          manager.log.info(
            'Received before composer auth update:',
            metadata,
            Boolean(firebaseUser),
          );

          trackLiveAction('fb_composer_before_connection_auth_update', {
            isDisposed: manager.IS_DISPOSED,
            name: appName,
            sessionId: sessionId,
            user: Boolean(firebaseUser),
          });
        },
        onIdTokenChanged: (firebaseUser) => {
          manager.log.info(
            'Received composer ID token changed event auth update:',
            metadata,
            Boolean(firebaseUser),
          );
        },
        onAuthStateChanged: (firebaseUser) => {
          trackLiveAction('fb_composer_connection_auth_update', {
            isDisposed: manager.IS_DISPOSED,
            name: appName,
            user: Boolean(firebaseUser),
          });

          if (manager.IS_DISPOSED) {
            return manager.log.info('Received composer auth update after disposal:', metadata);
          } else {
            manager.log.info('Received interaction auth update:', metadata);
          }

          manager.setContext({
            composerConnection: manager.context.composerConnection.asMerged({
              user: firebaseUser,
            }),
          });
        },
        onConnectionStateChanged: (snapshot: TFirebaseSnapshot) => {
          if (manager.IS_DISPOSED) {
            return manager.log.info('Received composer status change after disposal:', metadata);
          }

          const isConnected: boolean = snapshot.val() === true;

          // Track firebase connect disconnect if it is actual event, not first two on init.
          if (composerAppReadyNotified) {
            trackLiveAction(isConnected ? 'fb_composer_connected' : 'fb_composer_disconnected');
          }

          manager.log.info('Composer firebase connection state change:', isConnected, metadata);
          manager.setContext({
            composerConnection: manager.context.composerConnection.asMerged({
              isConnected,
            }),
          });

          if (isConnected && !composerAppReadyNotified && !manager.IS_DISPOSED) {
            manager.log.info('Composer firebase app is ready:', metadata);
            trackLiveAction('fb_composer_app_ready');

            composerAppReadyNotified = true;

            manager.emitSignal<TFirebaseComposerAppReadySignalEvent['data']>({
              type: ELiveRealtimeSignal.FIREBASE_COMPOSER_APP_READY,
              data: {
                firebase,
                app: manager.composerApp as TFirebaseApp,
                config,
                sessionId,
                sessionFolder,
              },
            });
          }
        },
        onClockSkewChanged: (snapshot) => {
          liveApplicationConfig.TIMING.CLOCK_SKEW = snapshot.val() || 0;
        },
      },
    );

    manager.composerApp = app;

    if (manager.IS_DISPOSED) {
      manager.log.info('Composer apps de-synced with fast updates, need unsubscribing');
      unSubscribers.forEach((it) => it());
    } else {
      manager.log.info('Composer apps created:', metadata);
      manager.addUnSubscribers(unSubscribers);
    }
  } catch (error) {
    manager.log.error('Failed to authorize composer firebase:', error, metadata);
    trackLiveError(error, {
      method: 'createComposerConnection',
      category: ELiveErrorCategory.FIREBASE,
    });
  }

  trackLiveAction('fb_composer_connection_created');
}

/**
 * Event information loaded, fetch chat credentials and start firebase for composer application.
 */
export async function initializeComposerConnection<T>(
  manager: AbstractFirebaseManager<T>,
  signal: TComposerSessionReadySignalEvent,
): Promise<void> {
  manager.log.info('Session information loaded, fetching composer credentials:', signal.data);

  try {
    const {
      firebase: { config, jwt, appName },
      sessionId,
      user,
    } = await manager.loadComposerConfig(signal);

    manager.setContext({
      composerCredentials: {
        config: {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          databaseURL: config.databaseUrl,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
        },
        token: jwt,
        sessionFolder: `${config.graphicsStorageFolder}${sessionId}`,
        sessionId,
        appName: generateFirebaseApplicationName(
          manager.uuid,
          appName ?? `vimeo-composer-${manager.uuid}`,
        ),
        user,
      },
      isComposerReady: true,
    });

    manager.log.info(
      'Composer config credentials ready:',
      manager.context.composerCredentials,
      config,
    );

    await tryToSetupComposerConnection(manager);
  } catch (error) {
    manager.log.error('Failed to load composer FB config:', error);
    manager.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });

    trackLiveError(error, {
      method: 'initializeComposerConnection',
      category: ELiveErrorCategory.FIREBASE,
      data: { signal },
    });
  }
}
